/**
 * ============================================================================
 * IM8 PRO PAGE - WORLD-CLASS ANIMATION ORCHESTRATOR
 * Cinematic storytelling through motion
 * 
 * ONLY for PRO-specific sections:
 * - im8-pro-hero
 * - im8-pro-clinical-validation (im8-clinical)
 * - im8-pro-features
 * - im8-pro-upgrade-spectrum
 * - im8-pro-welcome
 * - im8-pro-faq
 * ============================================================================
 */

(function() {
  'use strict';

  // ===== CONFIGURATION =====
  const CONFIG = {
    threshold: 0.15,
    rootMargin: '0px 0px -20% 0px',
    
    sections: {
      hero: '.im8-pro-hero',
      clinical: '.im8-clinical',
      features: '.im8-features',
      spectrum: '.im8-spectrum',
      welcome: '.im8-pro-welcome',
      proFaq: '.im8-pro-faq'
    },
    
    classes: {
      animated: 'is-animated',
      visible: 'is-visible',
      complete: 'pro-animation-complete'
    },
    
    unobserveAfterReveal: true,
    // Longest hero transition: 650ms delay + 900ms duration ≈ 1550ms
    removeWillChangeDelay: 1800,
    
    parallaxEnabled: true,
    parallaxIntensity: 0.05
  };

  // ===== DEVICE & PREFERENCE DETECTION =====
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = window.innerWidth < 750;
  const isLowEndDevice = () => {
    if (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2) return true;
    if (navigator.deviceMemory && navigator.deviceMemory <= 2) return true;
    return false;
  };

  // ===== ANIMATION ORCHESTRATOR CLASS =====
  class ProAnimationOrchestrator {
    constructor() {
      this.observer = null;
      this.observedElements = new Set();
      this.initialized = false;
      this.heroAnimated = false;
      this.scrollY = 0;
      this.ticking = false;
    }

    /**
     * Initialize the animation system
     */
    init() {
      if (this.initialized || prefersReducedMotion) {
        if (prefersReducedMotion) {
          this.showAllImmediately();
        }
        return;
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    /**
     * Setup observers and start animations.
     * Hero is triggered immediately via rAF (no IntersectionObserver overhead).
     * Other sections still use IO for scroll-triggered reveals.
     */
    setup() {
      this.createSectionObserver();
      this.observeSections();

      if (CONFIG.parallaxEnabled && !isMobile && !isLowEndDevice()) {
        this.setupParallax();
      }

      this.initialized = true;

      // Trigger hero on the next frame so the browser has flushed
      // the initial render and every child is at opacity:0 / transform.
      // Double-rAF ensures we're past the first paint.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.animateHero();
        });
      });
    }

    /**
     * Create observer for other sections
     */
    createSectionObserver() {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.revealSection(entry.target);
            
            if (CONFIG.unobserveAfterReveal) {
              this.observer.unobserve(entry.target);
              this.observedElements.delete(entry.target);
            }
          }
        });
      }, {
        threshold: CONFIG.threshold,
        rootMargin: CONFIG.rootMargin
      });
    }

    /**
     * Observe all PRO-specific animated sections
     */
    observeSections() {
      // Only observe PRO-specific sections
      const sectionSelectors = [
        CONFIG.sections.clinical,
        CONFIG.sections.features,
        CONFIG.sections.spectrum,
        CONFIG.sections.welcome,
        CONFIG.sections.proFaq
      ];

      sectionSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          if (!element.classList.contains(CONFIG.classes.visible)) {
            this.observer.observe(element);
            this.observedElements.add(element);
          }
        });
      });

      console.log(`👀 Observing ${this.observedElements.size} PRO sections for animations`);
    }

    /**
     * Animate hero section with cinematic entrance
     */
    animateHero() {
      if (this.heroAnimated) return;
      const hero = document.querySelector(CONFIG.sections.hero);
      if (!hero) return;

      this.heroAnimated = true;
      hero.classList.add(CONFIG.classes.animated);

      setTimeout(() => {
        hero.classList.add(CONFIG.classes.complete);
      }, CONFIG.removeWillChangeDelay);
    }

    /**
     * Reveal a section with animation
     */
    revealSection(section) {
      section.classList.add(CONFIG.classes.visible);

      // Remove will-change after animation
      setTimeout(() => {
        section.classList.add(CONFIG.classes.complete);
      }, CONFIG.removeWillChangeDelay);
    }

    /**
     * Setup parallax scrolling effects
     */
    setupParallax() {
      const hero = document.querySelector(CONFIG.sections.hero);
      if (!hero) return;

      // Use passive scroll listener for performance
      window.addEventListener('scroll', () => {
        this.scrollY = window.scrollY;
        
        if (!this.ticking) {
          requestAnimationFrame(() => {
            this.updateParallax(hero);
            this.ticking = false;
          });
          this.ticking = true;
        }
      }, { passive: true });
    }

    /**
     * Update parallax positions
     */
    updateParallax(hero) {
      const heroRect = hero.getBoundingClientRect();
      const heroHeight = hero.offsetHeight;
      
      // Only apply parallax when hero is in view
      if (heroRect.bottom > 0 && heroRect.top < window.innerHeight) {
        const scrollProgress = Math.min(this.scrollY / heroHeight, 1);
        hero.style.setProperty('--hero-parallax-depth', scrollProgress);
      }
    }

    /**
     * Show all elements immediately (for reduced motion)
     */
    showAllImmediately() {
      Object.values(CONFIG.sections).forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          element.classList.add(CONFIG.classes.animated);
          element.classList.add(CONFIG.classes.visible);
          element.classList.add(CONFIG.classes.complete);
        });
      });
    }

    /**
     * Refresh observations (for dynamically loaded content)
     */
    refresh() {
      this.observeSections();
    }

    /**
     * Destroy and cleanup
     */
    destroy() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.observedElements.clear();
      this.initialized = false;
    }
  }

  // ===== INITIALIZE =====
  
  // Create global instance
  window.IM8ProAnimations = new ProAnimationOrchestrator();
  
  // Auto-initialize
  window.IM8ProAnimations.init();

  // Allow manual refresh
  window.refreshIM8ProAnimations = function() {
    if (window.IM8ProAnimations) {
      window.IM8ProAnimations.refresh();
    }
  };

  // ===== CLEANUP ON PAGE UNLOAD =====
  window.addEventListener('beforeunload', () => {
    if (window.IM8ProAnimations) {
      window.IM8ProAnimations.destroy();
    }
  });

  // ===== PERFORMANCE MONITORING (Development) =====
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    window.addEventListener('load', () => {
      if (window.performance && window.performance.timing) {
        const loadTime = window.performance.timing.loadEventEnd - window.performance.timing.navigationStart;
        console.log(`⚡ IM8 PRO page loaded in ${loadTime}ms with premium animations`);
      }
    });
  }

})();

/**
 * ============================================================================
 * END OF IM8 PRO ANIMATION ORCHESTRATOR
 * Performance-first • Accessibility-aware • Cinematically refined ✨
 * ============================================================================
 */
